import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { LOGO } from "../../lib/media";
import { useI18n } from "../../i18n/LanguageContext";
import { routePath, switchLangPath } from "../../lib/routes";
import { SITE } from "../../content/site";
import { track, EVENTS } from "../../lib/analytics";
import { LangSwitcher, LANG_NAMES, LANG_ORDER } from "../LangSwitcher";

const PRIMARY = ["events", "ventures", "technology", "about", "blog"];
const ease = [0.16, 1, 0.3, 1];

export default function Nav({ solid = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [section, setSection] = useState("featured");
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t, lang } = useI18n();
  const c = SITE[lang].nav.ventures ? SITE[lang] : SITE.en;
  const menu = c.menu;
  const cards = menu.cards[section] || menu.cards.featured;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    if (open) window.__lenis?.stop(); else window.__lenis?.start();
    return () => { document.body.style.overflow = ""; window.__lenis?.start(); };
  }, [open]);
  useEffect(() => { setOpen(false); setSection("featured"); }, [pathname]);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const light = solid || scrolled;
  const txt = light ? "text-[#050505]" : "text-white";

  const touchRef = React.useRef(null);
  const onPanelTouchStart = (e) => {
    const t = e.touches && e.touches[0];
    if (!t || e.target.closest("[data-swipe-ignore]")) { touchRef.current = null; return; }
    touchRef.current = { x: t.clientX, y: t.clientY };
  };
  const onPanelTouchEnd = (e) => {
    const s = touchRef.current;
    const t = e.changedTouches && e.changedTouches[0];
    if (!s || !t) return;
    touchRef.current = null;
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    if (dx < -70 && Math.abs(dx) > Math.abs(dy) * 1.5) setOpen(false);
  };

  const switchLang = (target) => {
    if (target === lang) return;
    track(EVENTS.LANGUAGE_CHANGED, { from: lang, to: target });
    navigate(switchLangPath(pathname, target));
  };

  const Card = ({ card, i, testPrefix }) => card.logos ? (
    <Link to={routePath(card.route, lang)} data-testid={`${testPrefix}-${i}`}
      className="group flex aspect-[4/3] w-full flex-col items-center justify-center gap-3.5 overflow-hidden rounded-xl border border-black/6 bg-white px-8 shadow-sm transition-shadow hover:shadow-md">
      <img src="/assets/products/aos-wordmark.png" alt="AOS Equestrian" loading="lazy" className="h-4 w-auto max-w-[62%] object-contain md:h-5" />
      <img src="/assets/products/arena-wordmark.png" alt="Arena" loading="lazy" className="h-4 w-auto max-w-[46%] object-contain md:h-5" />
      <img src="/assets/products/centauros-wordmark.png" alt="Centauros" loading="lazy" className="h-4 w-auto max-w-[62%] object-contain md:h-5" />
      <span className="mt-1.5 text-[12px] font-semibold text-[#141514]">{card.t}</span>
    </Link>
  ) : (
    <Link to={routePath(card.route, lang)} data-testid={`${testPrefix}-${i}`}
      className="group relative block w-full shrink-0 snap-center overflow-hidden rounded-xl bg-white shadow-sm">
      <img src={card.img} alt={card.t} loading="lazy"
        className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]" />
      <span className="absolute inset-x-2 bottom-2 rounded-lg bg-white/90 px-3 py-2 text-center text-[13px] font-semibold text-[#141514] backdrop-blur-sm">
        {card.t}
      </span>
    </Link>
  );


  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        light ? "bg-white/90 backdrop-blur-xl border-b border-black/5" : "bg-transparent"
      }`}
    >
      <div className="relative mx-auto flex max-w-[1600px] items-center justify-between px-5 py-4 md:px-10">
        <button data-testid="nav-menu-btn" onClick={() => setOpen(true)} aria-label="Open menu" aria-expanded={open}
          className={`flex items-center gap-2.5 text-sm font-light transition-colors ${txt}`}>
          <Menu size={20} strokeWidth={1.75} />
          <span className="hidden sm:inline">{menu.open}</span>
        </button>

        <Link data-testid="nav-logo" to={routePath("home", lang)} aria-label="Equestrian Partners — Home"
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <img src={light ? LOGO.horizontalBlack : LOGO.horizontalWhite} alt="Equestrian Partners"
            className="h-5 w-auto shrink-0 object-contain md:h-6" />
        </Link>

        <div className="flex items-center gap-3 md:gap-6">
          <LangSwitcher lang={lang} dark={!light} testid="language-selector" />
          <button data-testid="nav-book-btn"
            onClick={() => { track(EVENTS.CTA_CLICK, { cta: "nav_book" }); navigate(routePath("book", lang)); }}
            className={`hidden text-sm font-light transition-opacity hover:opacity-70 sm:inline ${txt}`}>
            {t("nav.book")}
          </button>
        </div>
      </div>

      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div key="menu" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.35 }} className="fixed inset-0 z-[70]" data-testid="nav-menu-overlay">
              <div className="absolute inset-0 bg-black/25 backdrop-blur-md" onClick={() => setOpen(false)} />

              <button data-testid="nav-menu-close" onClick={() => setOpen(false)} aria-label="Close menu"
                className="absolute right-5 top-5 z-20 flex h-11 w-11 items-center justify-center rounded-full border border-black/10 bg-white text-[#141514] shadow-lg">
                <X size={20} strokeWidth={1.75} />
              </button>

              <div className="absolute inset-y-0 left-0 flex w-full lg:w-auto">
                {/* Primary panel */}
                <motion.div initial={{ x: -60, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: -40, opacity: 0 }}
                  transition={{ duration: 0.5, ease }} data-lenis-prevent
                  onTouchStart={onPanelTouchStart} onTouchEnd={onPanelTouchEnd}
                  className="relative flex h-full w-full flex-col overflow-y-auto overscroll-contain bg-white px-7 pb-10 pt-[4.5rem] sm:px-10 lg:w-[400px] xl:w-[440px] lg:pt-24">
                  {/* Mobile featured cards rail — first, like Rolex mobile */}
                  <div data-lenis-prevent data-swipe-ignore className="-mx-7 mb-9 flex min-h-[180px] snap-x snap-mandatory gap-3 overflow-x-auto px-7 pb-1 lg:hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    {cards.map((card, i) => (
                      <div key={card.t} className="w-[228px] shrink-0 snap-start scroll-ml-7">
                        <Card card={card} i={i} testPrefix="nav-menu-card-m" />
                      </div>
                    ))}
                  </div>
                  <nav aria-label="Main" data-testid="nav-mobile-menu">
                    <button data-testid="nav-menu-featured" onMouseEnter={() => setSection("featured")} onClick={() => setSection("featured")}
                      className={`block py-2.5 text-left font-display text-[22px] font-normal tracking-tight transition-colors md:text-2xl ${
                        section === "featured" ? "text-[#0e7c6b]" : "text-[#141514] hover:text-[#0e7c6b]"}`}>
                      {menu.featured}
                    </button>
                    {PRIMARY.map((k) => (
                      <Link key={k} to={routePath(k === "blog" ? "blog" : k, lang)} data-testid={`nav-menu-${k}`}
                        onMouseEnter={() => setSection(k)}
                        className={`block py-2.5 font-display text-[22px] font-normal tracking-tight transition-colors md:text-2xl ${
                          section === k ? "text-[#0e7c6b]" : "text-[#141514] hover:text-[#0e7c6b]"}`}>
                        {c.nav[k]}
                      </Link>
                    ))}
                  </nav>

                  <div className="mt-auto flex flex-wrap items-center gap-1 pt-12 text-sm font-medium">
                    {LANG_ORDER.map((l) => (
                      <button key={l} data-testid={`nav-menu-lang-${l}`} onClick={() => { setOpen(false); switchLang(l); }}
                        aria-label={LANG_NAMES[l]}
                        className={`px-2 py-1 uppercase ${lang === l ? "text-[#050505]" : "text-black/35 hover:text-black/60"}`}>
                        {l}
                      </button>
                    ))}
                  </div>

                  <img src={LOGO.horizontalBlack} alt="Equestrian Partners" loading="lazy"
                    className="mx-auto mt-8 h-7 w-auto shrink-0 object-contain" />
                </motion.div>

                {/* Featured cards panel (desktop) */}
                <motion.div initial={{ x: -40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ opacity: 0 }}
                  transition={{ duration: 0.55, ease, delay: 0.08 }} data-lenis-prevent
                  className="hidden h-full w-[470px] overflow-y-auto overscroll-contain bg-[#f6f6f4] px-8 pb-10 pt-24 lg:block xl:w-[560px]">
                  <AnimatePresence mode="wait">
                    <motion.div key={section} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: { duration: 0.18 } }}
                      transition={{ duration: 0.45, ease }} className="grid grid-cols-2 gap-4">
                      {cards.map((card, i) => (
                        <motion.div key={card.t} initial={{ opacity: 0, scale: 0.985 }} animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.55, ease, delay: 0.05 + i * 0.07 }}>
                          <Card card={card} i={i} testPrefix="nav-menu-card" />
                        </motion.div>
                      ))}
                    </motion.div>
                  </AnimatePresence>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </header>
  );
}
